import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash-es";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useNotification } from "~hooks/useNotification";
import { useApi } from "~shared/api";
import { useRecalculateSeries } from "../../hooks";
import { transformFixtureListResponse } from "../../utilities";
import type { FixturePartialUpsert } from "../types";
import { getFixtureListQueryKey } from "./useFetchAllSeasonFixtures";

export const useUpdateListFixture = () => {
  const { sport = "", organizationId = "", seasonId = "" } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { mutate: recalculateSeries } = useRecalculateSeries();
  const { formatMessage } = useIntl();
  const notification = useNotification();
  return useMutation({
    mutationFn: async (upsertModel: FixturePartialUpsert) => {
      return await mutateData({
        url: `${sport}/o/${organizationId}/fixtures/${upsertModel.fixtureId}?include=seasons,entities,persons,venues,seasonStages,seasonPools,seasonSeries,seasonRounds`,
        method: "PUT",
        data: upsertModel.upsertData,
      });
    },
    onSuccess: (response: any, upsertModel: FixturePartialUpsert) => {
      if (response?.data && response.data.length > 0) {
        const transformedFixtures = transformFixtureListResponse(response);
        const updatedFixture = transformedFixtures[0];

        if (!isEmpty(upsertModel.oldSeriesCode) && upsertModel.oldSeriesCode !== upsertModel.upsertData?.seriesCode) {
          recalculateSeries(upsertModel.oldSeriesCode ?? "");
        }

        if (!isEmpty(upsertModel.upsertData?.seriesCode)) {
          recalculateSeries(upsertModel.upsertData.seriesCode);
        }

        queryClient.setQueryData(getFixtureListQueryKey({ sport, organizationId, seasonId }), (oldFixtures: []) =>
          (oldFixtures || []).map((oldFixture: any) => (oldFixture.fixtureId === updatedFixture.fixtureId ? updatedFixture : oldFixture)),
        );

        notification.success({
          message: formatMessage({
            id: "fixtureListPage.upsertFixtureModel.notifications.onUpdated",
          }),
          placement: "bottomRight",
        });
      }
    },
  });
};
